import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, ModalContent, ModalTitle } from "@safers/ui/organisms";
import { Mic } from "@safers/ui/atoms";
import {
  useVoiceRecognition,
  VOICE_ERROR_MESSAGE,
  type VoiceError,
} from "@/hooks/useVoiceRecognition";
import { useCommandInterpreter } from "@/hooks/useCommandInterpreter";
import { useCurrentSiteId } from "@/hooks/useCurrentSiteId";
import { useBackButtonClose } from "@/hooks/useBackButtonClose";
import type { CommandSite, Interpretation, TargetKind } from "@/lib/command/resolve";
import { RecordingIndicator } from "./RecordingIndicator";

interface CommandModalProps {
  open: boolean;
  onClose: () => void;
  /** 열자마자 듣기 시작 — 모바일 마이크 진입점이 쓴다. */
  autoListen?: boolean;
}

/** 답을 읽을 틈. 없으면 화면이 그냥 튄 것처럼 보인다. */
const READ_MS = 550;

interface Turn {
  said: string;
  reply: string;
}

/**
 * 명령 팔레트.
 *
 * **고르는 화면이 아니라 말하는 화면이다.** 갈 수 있는 화면을 늘어놓지 않는다 — 그건
 * 우리 라우트 표를 그대로 보여주는 것이고, "말하면 알아서 간다" 는 전제와 어긋난다.
 * 되묻는 건 현장이 여럿일 때뿐이고, 그때도 화면 종류는 묻지 않는다.
 */
export function CommandModal({ open, onClose, autoListen = false }: CommandModalProps) {
  const [value, setValue] = useState("");
  const [turn, setTurn] = useState<Turn | null>(null);
  /*
    한 번 거부당하면 그 뒤로는 **텍스트 모드로 연다.** 누를 때마다 브라우저가 조용히
    막아 아무 일도 안 일어나는 상태가 최악이다. 세션 안에서만 기억하면 된다 —
    사용자가 설정에서 허용으로 바꾸면 새로고침으로 풀린다.
  */
  const [voiceBlocked, setVoiceBlocked] = useState(false);
  const [asking, setAsking] = useState<{ sites: CommandSite[]; kind: TargetKind } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const { status, isSupported, start, cancel } = useVoiceRecognition();
  const isRecording = status === "listening";
  const voiceOpen = status !== "idle";

  /* 음성이 안 되는 기기이거나 한 번 거부당한 뒤에는 마이크로 들어와도 텍스트 모드다.*/
  const listensOnOpen = autoListen && isSupported && !voiceBlocked;

  // 현장을 말하지 않았을 때 기준이 되는 현장. 경로를 직접 맞춰 읽는다 —
  // useParams 는 이 컴포넌트가 있는 레이아웃에서 자식 파라미터를 못 본다.
  const currentSiteId = useCurrentSiteId();
  const { runBest, runForSite, pickSite } = useCommandInterpreter(currentSiteId, open);

  /*
    아래 셋은 **수동 메모를 걸지 않는다.** React Compiler 가 빌드 타임에 심고, effect
    의존성으로 쓰이지 않아 안정된 참조가 계약인 자리도 아니다.
  */
  const reset = () => {
    clearTimeout(timer.current);
    cancel();
    setValue("");
    setTurn(null);
    setAsking(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useBackButtonClose(open, handleClose);

  const cancelVoice = () => {
    if (autoListen) handleClose();
    else cancel();
  };

  const apply = (said: string, result: Interpretation) => {
    setTurn({ said, reply: result.say });
    setValue("");

    if (result.type === "ask-site") {
      setAsking({ sites: result.sites, kind: result.pending });
      return;
    }
    setAsking(null);
    if (result.type !== "go") return;

    // 답을 잠깐 보여주고 움직인다 — 바로 닫으면 무엇으로 이해했는지 확인할 틈이 없다.
    // 앞 예약이 남아 있으면 먼저 지운다 — 안 지우면 참조를 잃은 채 살아남아
    // 뒤늦게 발화해 사용자가 마지막에 지시한 화면을 덮어쓴다.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (result.path !== location.pathname) navigate(result.path, { replace: true });
      handleClose();
    }, READ_MS);
  };

  /**
   * 명령 실행.
   *
   * **후보를 여러 개 받는다** — 타이핑은 하나, 음성은 인식기가 확신 순으로 준 여럿.
   * 두 입력이 같은 함수를 지나야 음성만 다르게 동작하는 일이 안 생긴다.
   */
  const runCandidates = (candidates: string[]) => {
    const texts = candidates.map((t) => t.trim()).filter(Boolean);
    if (!texts.length) return;

    /*
      되묻는 중이면 그 답으로 먼저 읽는다. 칩을 누르지 않고 이름을 말하거나 타이핑해도
      원래 요청한 화면(CCTV 등)이 유지돼야 한다 — 새로 해석하면 화면 키워드가 없어
      대시보드로 떨어진다.
    */
    if (asking) {
      for (const said of texts) {
        const picked = pickSite(said, asking.sites);
        if (picked) {
          apply(said, runForSite(asking.kind, picked));
          return;
        }
      }
    }

    const { said, result } = runBest(texts);
    if (said) apply(said, result);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  /*
    인식 결과는 effect 밖(인식기 콜백)에서 들어오는데, 그때 최신 상태(`asking` 등)를
    봐야 한다. 의존성에 넣으면 인식 중에 effect 가 다시 돌아 인식기가 뒤엉키므로
    ref 로 최신 것만 잡는다.
  */
  const latest = useRef(runCandidates);
  useEffect(() => {
    latest.current = runCandidates;
  });

  // React Compiler 가 켜져 있어도 새 코드에 수동 메모를 넣지 않는 게 원칙이지만,
  // 여기는 예외다 — effect 의존성으로 쓰여 **안정된 참조 자체가 계약**인 자리다.
  const startVoice = useCallback(() => {
    /*
      말하기 전에 키보드를 내린다. 입력창에 포커스가 남아 있으면 폰에서 녹음 화면
      뒤로 소프트 키보드가 그대로 떠 있어, 화면 절반이 가린 채 "말씀하세요" 가 뜬다.
      데스크톱에는 아무 영향이 없다.
    */
    inputRef.current?.blur();
    start({
      onResult: (transcript, isFinal, alternatives) => {
        setValue(transcript);
        if (isFinal) latest.current(alternatives);
      },
      onError: (kind: VoiceError) => {
        // 실패를 삼키지 않는다 — 마이크를 눌렀는데 아무 일도 안 일어나면
        // 사용자는 "그냥 검색창이 떴다" 로 읽고 같은 행동을 반복한다.
        if (kind === "not-allowed" || kind === "not-supported") setVoiceBlocked(true);
        setTurn({ said: "", reply: VOICE_ERROR_MESSAGE[kind] });
      },
    });
  }, [start]);

  /*
    열 때 한 번만 건다. 이중 시작은 훅이 막지만(시작 전 이전 인스턴스를 abort 한다),
    거부당한 뒤에도 계속 시도하면 브라우저가 조용히 막아 아무 일도 안 일어난다.
  */
  const autoListened = useRef(false);
  useEffect(() => {
    if (!open) {
      autoListened.current = false;
      return;
    }
    if (!listensOnOpen || autoListened.current) return;
    autoListened.current = true;
    startVoice();
  }, [open, listensOnOpen, startVoice]);

  /*
    다크 콘솔. 앱의 다크 헤더와 같은 값(#0F1117 · #2A2D3A)을 쓴다 — 새 색을 만들지 않으면서
    밝은 관제 화면 위에서 **다른 층**으로 읽히게 하려는 것이다. 흰 패널이면 떠 있는 느낌을
    그림자에만 기대야 해서 큰 화면에서 존재감이 안 생긴다.
  */
  return (
    <Modal open={open} onOpenChange={(next) => !next && handleClose()}>
      <ModalContent
        showClose={false}
        aria-describedby={undefined}
        className="top-[8vh] w-[36rem] max-w-[calc(100vw-1.5rem)] sm:top-[16vh] sm:max-w-[calc(100vw-2rem)] translate-y-0 overflow-hidden border border-[#2A2D3A] bg-[#0F1117] p-0 shadow-[0_1.5rem_3rem_-0.75rem_rgba(0,0,0,0.55)]"
        onOpenAutoFocus={(e) => {
          if (!listensOnOpen) return;
          e.preventDefault();
          if (e.currentTarget instanceof HTMLElement) e.currentTarget.focus();
        }}
        onInteractOutside={(e) => {
          if (voiceOpen) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (voiceOpen) {
            e.preventDefault();
            cancelVoice();
          }
        }}
      >
        <ModalTitle className="sr-only">Safers AI</ModalTitle>

        {(turn || asking) && (
          <div className="min-w-0 space-y-3 border-b border-[#2A2D3A] px-5 pb-4 pt-5">
            {turn?.said && (
              <p className="text-right text-sm">
                {/*
                  음성 전사문은 **띄어쓰기 없이** 오는 일이 흔하다("제주현장보여줘").
                  `break-keep` 만 걸면 그걸 한 단어로 보고 안 끊어 말풍선이 넘쳐 잘린다 —
                  무엇으로 알아들었는지 확인할 유일한 단서가 사라진다.
                  띄어쓰기가 있으면 거기서 끊고, 없으면 넘치기 직전에 끊는다.
                */}
                <span className="inline-block max-w-[85%] break-keep [overflow-wrap:anywhere] rounded-2xl rounded-br-sm bg-white/10 px-3 py-1.5 text-left text-white/80">
                  {turn.said}
                </span>
              </p>
            )}
            {turn?.reply && (
              <div className="flex flex-col gap-1">
                <span className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-brand">
                  <span aria-hidden className="size-1.5 rounded-full bg-brand" />
                  Safers AI
                </span>
                <p className="break-keep [overflow-wrap:anywhere] text-[0.9375rem] text-white">
                  {turn.reply}
                </p>
              </div>
            )}
            {/*
              음성이 막혔으면 텍스트로 가라고 대놓고 말한다. 마이크 버튼은 그대로 두되
              (설정을 바꾸고 새로고침하면 다시 된다) 여기서 길을 알려 준다.
            */}
            {voiceBlocked && !asking && (
              <p className="break-keep text-xs text-white/40">
                아래 입력창에 타이핑해도 똑같이 동작해요.
              </p>
            )}

            {asking && (
              <div className="scrollbar-visible flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                {asking.sites.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => apply(s.name, runForSite(asking.kind, s))}
                    className="max-w-full break-keep [overflow-wrap:anywhere] rounded-full border border-white/15 px-3 py-1.5 text-sm text-white/80 transition-colors hover:border-brand hover:bg-brand/10 hover:text-brand"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            runCandidates([value]);
          }}
          className="flex min-w-0 items-center gap-2 px-5 py-4"
        >
          {/*
            마이크로 들어왔으면 포커스를 주지 않는다 — 열자마자 듣기 시작하는데
            키보드까지 올라오면 녹음 화면이 그 위에 덮여 무슨 화면인지 안 읽힌다.
            타이핑하려면 입력창을 탭하면 된다. 음성이 안 되는 기기이거나 한 번
            거부당한 뒤에는 텍스트 모드로 여는 것이므로 그때는 포커스를 준다.
          */}
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus={!listensOnOpen}
            placeholder={isRecording ? "듣고 있어요…" : "Safers AI 에게 물어보세요"}
            aria-label="Safers AI 에게 물어보기"
            className="min-w-0 flex-1 bg-transparent text-base text-white outline-none placeholder:text-white/35"
          />

          {isSupported && (
            <button
              type="button"
              onClick={() => (isRecording ? cancel() : startVoice())}
              /*
                시작 요청 중에는 못 누른다. 첫 사용 시 권한 팝업 때문에 `onstart` 가
                수 초 뒤에 오는데, 그 사이 다시 누르면 인식기가 둘이 됐다.
              */
              disabled={status === "starting"}
              aria-label={isRecording ? "음성 인식 중지" : "음성 입력 시작"}
              className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors disabled:opacity-60 ${
                isRecording
                  ? "border-red-500 bg-red-500 text-white"
                  : "border-brand/30 bg-brand/12 text-brand hover:bg-brand/20"
              }`}
            >
              {isRecording && (
                <span className="absolute inset-0 animate-ping rounded-full bg-red-500/40" />
              )}
              {status === "starting" ? (
                <span className="size-3.5 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
              ) : (
                <Mic size="sm" className="relative" />
              )}
            </button>
          )}
        </form>

        {!turn && !asking && (
          <div className="flex min-w-0 items-center justify-between gap-3 border-t border-[#2A2D3A] px-5 py-2.5 text-xs text-white/35">
            <span className="min-w-0 flex-1 truncate">
              예) 김포 풍무 CCTV 보여줘 · 점검표 열어줘 · 전국
            </span>
            {/* 좁은 폰에서는 접는다 — 예시 문구가 잘리느니 워드마크를 버리는 게 낫다. */}
            <span className="hidden shrink-0 font-semibold uppercase tracking-[0.12em] text-white/20 sm:inline">
              Safers AI
            </span>
          </div>
        )}
      </ModalContent>

      {/*
        `starting` 부터 띄운다 — 마이크가 열리기 전에 말하면 첫 단어가 날아간다.
      */}
      {voiceOpen && (
        <RecordingIndicator transcript={value} onStop={cancelVoice} ready={isRecording} />
      )}
    </Modal>
  );
}
